import React from "react";
import { Box, Typography, LinearProgress, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../../../constants/investmentLeague";

const StackedCoinsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    <path d="M21 12v7" />
  </svg>
);

const UserCheckIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ReinvestSummaryCards = ({
  availableCredit = 0,
  allocatedAmount = 0,
  remainingCredit = 0,
  allocationPercentage = null,
  isServerConfirmed = false,
  isFullyAllocated = false,
  isOverAllocated = false,
  investorUnit = "MAC 2 - Hercules",
  eligibleUnitsCount = 10,
  previousQuarter = "Q2",
  upcomingQuarter = "Q3",
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const overAllocated = isOverAllocated || remainingCredit < 0;
  const fullyAllocated = isFullyAllocated || (remainingCredit === 0 && allocatedAmount > 0);

  const allocatedPercentage = allocationPercentage != null
    ? allocationPercentage
    : (availableCredit > 0 ? (allocatedAmount / availableCredit) * 100 : 0);

  const remainingPercentage = availableCredit > 0
    ? Math.max(0, ((availableCredit - allocatedAmount) / availableCredit) * 100)
    : 0;

  const cardStyle = {
    flex: "1 1 0",
    minWidth: { xs: "100%", sm: "calc(50% - 12px)", lg: "0" },
    backgroundColor: t.surface,
    border: `1px solid ${t.border2}`,
    borderRadius: "12px",
    p: 2.25,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "116px",
    boxShadow: isDark ? "0 4px 16px rgba(0, 0, 0, 0.25)" : "0 2px 8px rgba(3, 81, 77, 0.06)",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        mb: 2.5,
      }}
    >
      {/* Card 1: Q2 Credit Available */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StackedCoinsIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                letterSpacing: "0.05em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: t.text3,
                mb: 0.5,
              }}
            >
              {previousQuarter} CREDIT (AVAILABLE)
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: t.text,
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                mb: 0.75,
              }}
            >
              {formatCurrency(availableCredit)}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: t.text3 }}>
              This is your available balance to invest in {upcomingQuarter}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Card 2: Amount Allocated */}
      <Box sx={cardStyle}>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: "0.72rem",
                letterSpacing: "0.05em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: t.text3,
              }}
            >
              AMOUNT ALLOCATED
            </Typography>
            {isServerConfirmed ? (
              <Chip
                label="Server Confirmed"
                size="small"
                sx={{
                  height: "18px",
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "rgba(45, 138, 91, 0.10)",
                  color: isDark ? "#10B981" : "#2D8A5B",
                  border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.3)" : "rgba(45, 138, 91, 0.25)"}`,
                }}
              />
            ) : (
              <Chip
                label="Estimated"
                size="small"
                sx={{
                  height: "18px",
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.10)",
                  color: isDark ? "#FBBF24" : "#B45309",
                  border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.3)" : "rgba(245, 158, 11, 0.25)"}`,
                }}
              />
            )}
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: overAllocated ? (isDark ? "#EF4444" : "#DC2626") : t.text,
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
              mb: 0.75,
            }}
          >
            {formatCurrency(allocatedAmount)}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: overAllocated ? (isDark ? "#EF4444" : "#DC2626") : isDark ? "#10B981" : "#2D8A5B",
              fontWeight: 500,
              mb: 1,
            }}
          >
            {allocatedPercentage.toFixed(1)}% of available credit
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, allocatedPercentage))}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(3, 81, 77, 0.08)",
            "& .MuiLinearProgress-bar": {
              backgroundColor: overAllocated ? (isDark ? "#EF4444" : "#DC2626") : isDark ? "#10B981" : "#2D8A5B",
              borderRadius: 3,
            },
          }}
        />
      </Box>

      {/* Card 3: Remaining Credit */}
      <Box sx={cardStyle}>
        <Box>
          <Typography
            sx={{
              fontSize: "0.72rem",
              letterSpacing: "0.05em",
              fontWeight: 600,
              textTransform: "uppercase",
              color: t.text3,
              mb: 0.5,
            }}
          >
            REMAINING CREDIT
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: overAllocated ? (isDark ? "#EF4444" : "#DC2626") : t.text,
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
              mb: 0.75,
            }}
          >
            {overAllocated
              ? "$0"
              : fullyAllocated
              ? "$0"
              : remainingCredit < 0
              ? `-${formatCurrency(Math.abs(remainingCredit))}`
              : formatCurrency(remainingCredit)}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: overAllocated
                ? (isDark ? "#EF4444" : "#DC2626")
                : fullyAllocated
                ? (isDark ? "#10B981" : "#2D8A5B")
                : t.text3,
              fontWeight: 500,
              mb: 1,
            }}
          >
            {overAllocated
              ? "Exceeded available credit!"
              : fullyAllocated
              ? `Your full ${previousQuarter} credit has been allocated to ${upcomingQuarter}.`
              : `${remainingPercentage.toFixed(1)}% remaining`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={overAllocated ? 100 : remainingPercentage}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(3, 81, 77, 0.08)",
            "& .MuiLinearProgress-bar": {
              backgroundColor: overAllocated
                ? (isDark ? "#EF4444" : "#DC2626")
                : fullyAllocated
                ? (isDark ? "#10B981" : "#2D8A5B")
                : (isDark ? "#38BDF8" : t.accent),
              borderRadius: 3,
            },
          }}
        />
      </Box>

      {/* Card 4: Your Investor Unit */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.10)",
              border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.3)" : "rgba(245, 158, 11, 0.25)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserCheckIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                letterSpacing: "0.05em",
                fontWeight: 600,
                textTransform: "uppercase",
                color: t.text3,
                mb: 0.5,
              }}
            >
              YOUR INVESTOR UNIT
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: t.text,
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                mb: 0.75,
              }}
            >
              {investorUnit}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: t.text3 }}>
              You can invest in {eligibleUnitsCount} other units (cannot invest in yourself)
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReinvestSummaryCards;

